import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useAuth } from "../../../../../context/AuthContext";
import { useCustomerWorkspace } from "../../../../../context/CustomerWorkspaceContext";
import { useToast } from "../../../../../context/ToastContext";
import { debug } from "../../../../../utils/debugLogger";
import ConfirmDialog from "../../../../../components/Shared/ConfirmDialog";
import BlogEditorPanel from "./BlogEditorPanel";
import { DeleteIconButton } from "./DeleteIconButton";
import { listBlogDrafts, deleteBlogDraft } from "../services/wordpressBlogService";
import type { BlogPost } from "../types/wordpress.types";
import { formatScheduledForDisplay } from "../../../../../utils/scheduleTime";

type StatusFilter = "all" | "draft" | "scheduled" | "published";

/**
 * Don't revalidate on focus if the list was loaded more recently than this.
 * Returning to a tab fires visibilitychange AND focus, so without a floor the
 * same load would run twice. 30s is well under the scheduler's 30-minute cadence,
 * so it can never make the list meaningfully stale.
 */
const REVALIDATE_MIN_INTERVAL_MS = 30_000;

/** How often to re-check an overdue blog. The backend cron has 30-minute
 *  granularity, so a 1-minute poll is far more responsive than the thing it
 *  is waiting for — no value in going faster. */
const OVERDUE_POLL_INTERVAL_MS = 60_000;

/**
 * Give up polling after this many consecutive checks (~45 min at 1/min).
 *
 * A blog is normally published within one cron tick (≤30 min) of falling due, so
 * this only trips when something is genuinely wrong — e.g. a row stranded in
 * ENQUEUED by a crashed scheduler. Without a cap, that row would be polled for as
 * long as the tab stayed open. The backend recovers stranded rows itself after
 * 60 minutes (recover_stuck_blogs), so giving up here loses nothing.
 */
const MAX_OVERDUE_POLLS = 45;

/** Waiting on the backend scheduler: not yet resolved to PUBLISHED or FAILED. */
const isPending = (b: BlogPost): boolean =>
  b.status === "SCHEDULED" || b.status === "ENQUEUED";

/** Pending AND its moment has passed — the backend should be acting on it now. */
const isOverdue = (b: BlogPost, now: number): boolean => {
  if (!isPending(b) || !b.scheduledTime) return false;
  const due = new Date(b.scheduledTime).getTime();
  return !Number.isNaN(due) && due <= now;
};

/**
 * The card badge. Derived from `status` rather than the `published` boolean so a
 * scheduled or failed blog is not silently shown as a plain "Draft".
 *
 * `published` is still the flag the PUBLIC read API filters on — a SCHEDULED blog
 * has published=false and is therefore invisible on the customer's live site until
 * the scheduler actually publishes it.
 */
const getBlogBadge = (
  blog: BlogPost
): { label: string; className: string; title?: string } => {
  switch (blog.status) {
    case "PUBLISHED":
      return { label: "Published", className: "bg-theme-primary text-white" };
    case "SCHEDULED":
      return {
        label: "Scheduled",
        className: "bg-white text-theme-primary ring-1 ring-inset ring-theme-primary/30",
        title: blog.scheduledTime
          ? `Publishes ${formatScheduledForDisplay(blog.scheduledTime)}`
          : "Scheduled to publish",
      };
    case "ENQUEUED":
      return {
        label: "Publishing",
        className: "bg-white text-theme-primary ring-1 ring-inset ring-theme-primary/30",
        title: "Publishing right now",
      };
    case "FAILED":
      return {
        label: "Failed",
        className: "bg-red-100 text-red-700",
        title: blog.publishError || "The scheduled publish did not succeed",
      };
    default:
      return { label: "Draft", className: "bg-white text-theme-fg-muted ring-1 ring-inset ring-gray-200" };
  }
};

/** Format an ISO timestamp into a short, readable date for the card meta line. */
const formatDate = (iso: string): string => {
  const raw = (iso || "").trim();
  if (!raw) return "";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

/** Best-effort plain-text excerpt for the card, stripping Markdown noise. */
const previewText = (blog: BlogPost): string => {
  const fromExcerpt = (blog.excerpt || "").trim();
  if (fromExcerpt) return fromExcerpt;
  const body = (blog.content || "")
    .replace(/[#>*_`~\-]+/g, " ")
    .replace(/!\[[^\]]*\]\([^)]*\)/g, " ")
    .replace(/\[[^\]]*\]\([^)]*\)/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return body.length > 180 ? `${body.slice(0, 180)}…` : body;
};

/**
 * Blog review — the blog equivalent of the social-media Review tab.
 * Lists the customer's saved blogs (GET /content/{customer_id}/blogs) as cards
 * and opens the existing BlogEditorPanel for review & edit (PUT /blogs/{id}),
 * publish, or delete. No new backend is required — this is frontend-only wiring
 * over the endpoints the WordPress blog feature already exposes.
 */
const BlogReview: React.FC = () => {
  const { selectedCustomerId } = useCustomerWorkspace();
  const { idToken } = useAuth();
  const { showToast } = useToast();

  const [blogs, setBlogs] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");

  // Draft currently open in the editor drawer.
  const [editingBlog, setEditingBlog] = useState<BlogPost | null>(null);
  // Delete confirmation target.
  const [pendingDelete, setPendingDelete] = useState<BlogPost | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // When the list was last loaded, from ANY path (mount, refresh button, editor
  // close, focus). Used to coalesce redundant revalidations — a ref, not state,
  // because reading it must never trigger a render.
  const lastFetchAtRef = useRef(0);

  const fetchBlogs = useCallback(async () => {
    if (!selectedCustomerId || !idToken) return;
    lastFetchAtRef.current = Date.now();
    setLoading(true);
    try {
      const posts = await listBlogDrafts(selectedCustomerId, idToken);
      setBlogs(posts);
    } catch (error) {
      debug.error("Error fetching blogs:", error);
      showToast(
        error instanceof Error ? error.message : "Failed to load blogs",
        "error"
      );
    } finally {
      setLoading(false);
    }
  }, [selectedCustomerId, idToken, showToast]);

  useEffect(() => {
    fetchBlogs();
  }, [fetchBlogs]);

  /**
   * Revalidate when the user's attention returns to the app.
   *
   * Blogs are published by a backend cron, so this list goes stale on its own:
   * a blog shown as SCHEDULED may already be live on WordPress, and acting on
   * that stale row (e.g. "Cancel schedule") fails confusingly.
   *
   * Focus is the right trigger rather than polling: a blog can be scheduled days
   * ahead, so a timer would either poll for days or expire long before the blog
   * is due. Staleness only *matters* when the user looks at the screen — which is
   * exactly when this fires. Same rationale as React Query's refetchOnWindowFocus.
   *
   * Safe to reuse fetchBlogs: the skeleton is gated on `blogs.length === 0`, so a
   * revalidation with rows already on screen never flashes a loading state.
   */
  useEffect(() => {
    const revalidate = () => {
      // `focus` also fires on a hidden tab in some browsers; visibility is truth.
      if (document.visibilityState !== "visible") return;
      // The editor covers the list and already refetches on close — skip the
      // wasted query (list_blogs reads every blog for the customer).
      if (editingBlog) return;
      // visibilitychange and focus BOTH fire when returning to a tab; this also
      // stops rapid alt-tabbing from hammering the API.
      if (Date.now() - lastFetchAtRef.current < REVALIDATE_MIN_INTERVAL_MS) return;
      fetchBlogs();
    };

    document.addEventListener("visibilitychange", revalidate);
    window.addEventListener("focus", revalidate);
    return () => {
      document.removeEventListener("visibilitychange", revalidate);
      window.removeEventListener("focus", revalidate);
    };
  }, [fetchBlogs, editingBlog]);

  // Latest blogs, readable from inside the poll's interval closure. Held in a ref
  // so a refetch does NOT re-create the interval — that would reset its countdown
  // on every tick and it would never fire again.
  const blogsRef = useRef(blogs);
  useEffect(() => {
    blogsRef.current = blogs;
  }, [blogs]);

  /**
   * While a blog is OVERDUE, poll until the backend resolves it.
   *
   * Covers the one case focus-revalidation cannot: the user sits watching the
   * screen as the publish time passes, never blurring the window, so no focus
   * event ever fires.
   *
   * Self-limiting by design — this is why polling is acceptable here when a blind
   * timer would not be:
   *   - It starts only once a blog is actually DUE. A blog scheduled for next week
   *     costs nothing until next week.
   *   - It stops the moment the row resolves to PUBLISHED/FAILED (no pending blogs
   *     ⇒ the effect tears the interval down).
   *   - It is capped, so a row stranded by a crashed scheduler cannot be polled
   *     indefinitely.
   *   - It never runs in a hidden tab; returning to the tab is a focus event, which
   *     the handler above already covers.
   */
  const hasPendingBlogs = useMemo(() => blogs.some(isPending), [blogs]);

  useEffect(() => {
    if (!hasPendingBlogs || editingBlog) return;

    let polls = 0;
    const id = setInterval(() => {
      // A hidden tab is the focus handler's job, and browsers throttle timers
      // there anyway. Don't burn a request nobody is waiting on.
      if (document.visibilityState !== "visible") return;
      if (!blogsRef.current.some((b) => isOverdue(b, Date.now()))) return;
      if (polls >= MAX_OVERDUE_POLLS) return;
      polls += 1;
      fetchBlogs();
    }, OVERDUE_POLL_INTERVAL_MS);

    return () => clearInterval(id);
  }, [hasPendingBlogs, editingBlog, fetchBlogs]);

  const visibleBlogs = useMemo(() => {
    // "Draft" previously meant "not published", which would now sweep up
    // scheduled blogs too — a blog badged "Scheduled" sitting under the Draft
    // tab. Scheduled (and in-flight) blogs get their own bucket; FAILED stays
    // with drafts because it is a blog that still needs the user's attention.
    if (statusFilter === "draft")
      return blogs.filter(
        (b) => b.status !== "SCHEDULED" && b.status !== "ENQUEUED" && !b.published
      );
    if (statusFilter === "scheduled")
      return blogs.filter(
        (b) => b.status === "SCHEDULED" || b.status === "ENQUEUED"
      );
    if (statusFilter === "published") return blogs.filter((b) => b.published);
    return blogs;
  }, [blogs, statusFilter]);

  const confirmDelete = async () => {
    if (!pendingDelete || !selectedCustomerId) return;
    setIsDeleting(true);
    try {
      await deleteBlogDraft(selectedCustomerId, pendingDelete.id, idToken);
      setBlogs((prev) => prev.filter((b) => b.id !== pendingDelete.id));
      showToast("Blog deleted.", "success");
      setPendingDelete(null);
    } catch (error) {
      debug.error("Error deleting blog:", error);
      showToast(
        error instanceof Error ? error.message : "Failed to delete blog",
        "error"
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const filterTab = (value: StatusFilter, label: string) => (
    <button
      type="button"
      onClick={() => setStatusFilter(value)}
      className={`rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors ${
        statusFilter === value
          ? "bg-theme-primary text-white"
          : "text-theme-fg-muted hover:bg-theme-secondary/15"
      }`}
    >
      {label}
    </button>
  );

  if (!selectedCustomerId) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-16 text-center">
        <span className="material-icons text-4xl text-theme-fg-muted">
          storefront
        </span>
        <p className="text-theme-fg-muted">
          Select a business profile to review its blogs.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Toolbar: status filter + refresh */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-1.5">
          {filterTab("all", "All")}
          {filterTab("draft", "Drafts")}
          {filterTab("scheduled", "Scheduled")}
          {filterTab("published", "Published")}
        </div>
        <button
          type="button"
          onClick={fetchBlogs}
          disabled={loading}
          className="inline-flex items-center gap-1.5 rounded-lg border border-theme-border px-3 py-1.5 text-sm text-theme-fg-muted transition-colors hover:bg-theme-secondary/15 disabled:opacity-50"
        >
          <span
            className={`material-icons text-[18px] ${loading ? "animate-spin" : ""}`}
          >
            refresh
          </span>
          Refresh
        </button>
      </div>

      {/* Loading */}
      {loading && blogs.length === 0 ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="h-56 animate-pulse rounded-xl border border-theme-border bg-theme-background"
            />
          ))}
        </div>
      ) : visibleBlogs.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 py-16 text-center">
          <span className="material-icons text-4xl text-theme-fg-muted">
            article
          </span>
          <p className="font-medium text-theme-fg">No blogs yet</p>
          <p className="max-w-sm text-sm text-theme-fg-muted">
            Ask the AI agent in Chat to write a blog and save it — saved blogs
            show up here for review and editing.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {visibleBlogs.map((blog) => (
            <article
              key={blog.id}
              onClick={() => setEditingBlog(blog)}
              className="group flex cursor-pointer flex-col overflow-hidden rounded-xl border border-theme-border bg-theme-surface shadow-sm transition-all hover:border-theme-primary hover:shadow-md"
            >
              {/* Cover */}
              <div className="relative h-32 w-full shrink-0 overflow-hidden bg-theme-background">
                {blog.featuredImage ? (
                  <img
                    src={blog.featuredImage}
                    alt=""
                    className="h-full w-full object-cover"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center">
                    <span className="material-icons text-3xl text-theme-fg-muted/40">
                      article
                    </span>
                  </div>
                )}
                {(() => {
                  const badge = getBlogBadge(blog);
                  return (
                    <span
                      className={`absolute right-2 top-2 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${badge.className}`}
                      title={badge.title}
                    >
                      {badge.label}
                    </span>
                  );
                })()}
              </div>

              {/* Body */}
              <div className="flex flex-1 flex-col p-4">
                {blog.category && (
                  <p className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-theme-primary">
                    {blog.category}
                  </p>
                )}
                <h3 className="line-clamp-2 font-semibold leading-snug text-theme-fg">
                  {blog.title || "Untitled blog"}
                </h3>
                <p className="mt-1.5 line-clamp-3 flex-1 text-sm text-theme-fg-muted">
                  {previewText(blog)}
                </p>

                <div className="mt-3 flex items-center justify-between gap-2">
                  <span className="text-xs text-theme-fg-muted">
                    {formatDate(blog.updatedAt || blog.publishedAt)}
                  </span>
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingBlog(blog);
                      }}
                      className="inline-flex items-center gap-1 rounded-lg bg-theme-primary px-2.5 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-theme-primary-hover"
                    >
                      <span className="material-icons text-[15px]">edit</span>
                      Review & Edit
                    </button>
                    <DeleteIconButton
                      title="Delete blog"
                      onClick={(e) => {
                        e.stopPropagation();
                        setPendingDelete(blog);
                      }}
                    />
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      {/* Editor drawer — reuses the existing BlogEditorPanel (edit/save/publish) */}
      {editingBlog && selectedCustomerId && (
        <div className="fixed inset-0 z-[9999] flex justify-end">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => {
              setEditingBlog(null);
              fetchBlogs();
            }}
          />
          <div className="relative h-full w-full max-w-2xl shadow-2xl">
            <BlogEditorPanel
              customerId={selectedCustomerId}
              idToken={idToken}
              initialDraft={editingBlog}
              onClose={() => {
                setEditingBlog(null);
                fetchBlogs();
              }}
              onPublished={() => {
                showToast("Blog published.", "success");
                fetchBlogs();
              }}
              // Save / unpublish / schedule: keep the card behind the panel current.
              onPersisted={() => {
                fetchBlogs();
              }}
            />
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={Boolean(pendingDelete)}
        title="Delete blog?"
        message={
          <>
            This will permanently delete{" "}
            <strong>{pendingDelete?.title || "this blog"}</strong>. This action
            cannot be undone.
          </>
        }
        confirmLabel="Delete"
        isConfirming={isDeleting}
        onConfirm={confirmDelete}
        onCancel={() => {
          if (!isDeleting) setPendingDelete(null);
        }}
      />
    </div>
  );
};

export default BlogReview;
